"use client";

import * as React from "react";
import { useMediaQuery } from "../../hooks/use-media-query.js";
import { Popover } from '@mantine/core';
import { CentralizedDrawer } from "../global-drawer-stack.js";
import { cn } from "../../lib/utils.js";

export interface ResponsivePopoverProps {
  /** The trigger element */
  children: React.ReactNode;
  /** The content to display in the popover/drawer */
  content: React.ReactNode;
  /** Title for mobile drawer (optional) */
  title?: string;
  /** Whether the popover/drawer is open (controlled) */
  open?: boolean;
  /** Callback when the popover/drawer should be opened/closed (controlled) */
  onOpenChange?: (open: boolean) => void;
  /** Additional CSS classes for the content container */
  className?: string;
  /** Popover alignment (desktop only) */
  align?: "start" | "center" | "end";
  /** Popover side (desktop only) */
  popoverSide?: "top" | "right" | "bottom" | "left";
  /** Side for mobile drawer */
  drawerSide?: "top" | "bottom" | "left" | "right";
  /** Custom breakpoint for switching between popover and drawer */
  mobileBreakpoint?: string;
  /** Whether to disable the responsive behavior and always use popover */
  forcePopover?: boolean;
  /** Whether to disable the responsive behavior and always use drawer */
  forceDrawer?: boolean;
  /** Whether the trigger should be asChild (for custom trigger components) */
  asChild?: boolean;
  /** Whether to show close button in mobile drawer */
  showCloseButton?: boolean;
  /** Whether to use DrawerList for mobile (enables search and better scrolling) */
  useDrawerList?: boolean;
  /** Whether to enable search functionality in mobile drawer */
  searchable?: boolean;
  /** Search placeholder text */
  searchPlaceholder?: string;
  /** Search value (controlled) */
  searchValue?: string;
  /** Search change handler */
  onSearchChange?: (value: string) => void;
  /** Empty message when no results */
  emptyMessage?: string;
}

export const ResponsivePopover: React.FC<ResponsivePopoverProps> = ({
  children,
  content,
  title,
  open,
  onOpenChange,
  className = "",
  align = "center",
  popoverSide = "bottom",
  drawerSide = "bottom",
  mobileBreakpoint = "(max-width: 768px)",
  forcePopover = false,
  forceDrawer = false,
  asChild = false,
  showCloseButton = true,
  useDrawerList = false,
  searchable = false,
  searchPlaceholder = "Search...",
  searchValue,
  onSearchChange,
  emptyMessage = "No items found",
}) => {
  const isMobile = useMediaQuery(mobileBreakpoint);

  // Determine whether to use drawer or popover - ONLY on mobile
  const shouldUseDrawer = React.useMemo(() => {
    if (forcePopover) return false;
    if (forceDrawer) return true;
    // Only use drawer on mobile, not desktop
    return isMobile;
  }, [isMobile, forcePopover, forceDrawer]);

  if (shouldUseDrawer) {
    return (
      <>
        <div onClick={(e) => { e.stopPropagation(); onOpenChange?.(true); }}>
          {children}
        </div>
        <CentralizedDrawer
          drawerId="responsive-popover-drawer"
          side={drawerSide}
          className={className}
          title={title}
          showCloseButton={showCloseButton}
          enableSwipeGesture={true}
        >
          <div className="flex-1 overflow-y-auto p-4">
            {content}
          </div>
        </CentralizedDrawer>
      </>
    );
  }

  // Use Mantine Popover component for better control and performance
  return (
    <Popover
      opened={open}
      onClose={() => onOpenChange?.(false)}
      position={popoverSide}
      withArrow={true}
      withinPortal={true}
      zIndex={400}
    >
      <Popover.Target>
        {asChild ? children : <div>{children}</div>}
      </Popover.Target>
      <Popover.Dropdown
        className={cn(className?.includes("p-0") ? "" : "p-4", className)}
        style={{ zIndex: 400 }}
      >
        {content}
      </Popover.Dropdown>
    </Popover>
  );
};
